import { useImageResolver } from "@/api/hooks/useImageResolver";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { LastActivityLabel } from "@/components/Label/LastActivityLabel";
import { Column, Columns } from "@/design-system/Columns";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useTranslation } from "@/translations";

import type { CommunityFeedAudienceItem } from "./types";

type ResidentAudienceGroupOptionProps = {
  group: CommunityFeedAudienceItem;
  isPreview?: boolean;
};

export function ResidentAudienceGroupOption({
  group,
  isPreview = false,
}: ResidentAudienceGroupOptionProps): React.ReactNode {
  const resolveImage = useImageResolver();
  const { t } = useTranslation();
  const isGroup = group.type === "group";
  const audienceCount =
    isGroup && group.value.selectableAudience.length === 1
      ? t("page.message-feed.resident-create-post.form.audience.group.members", {
          count:
            group.value.selectableAudience[0].project?.audienceCount ??
            group.value.selectableAudience[0].projectConnection?.audienceCount ??
            0,
        })
      : undefined;

  return (
    <ContentBlock>
      <Columns gap="sm" align="center">
        {isGroup ? (
          <Column width="content">
            {group.value.isResidentGroup && group.value.image ? (
              <img
                src={resolveImage(group.value.image, "sm")}
                alt={group.value.name}
                className="size-icon-prominent rounded-full"
              />
            ) : (
              <span className="flex size-icon-prominent items-center justify-center">
                <GroupIcon icon={group.value.icon} />
              </span>
            )}
          </Column>
        ) : null}
        <Column>
          <Stack>
            <Columns gap="sm" align="center">
              <Column>
                <Text overflow="truncate">{group.value.name}</Text>
              </Column>
              {audienceCount ? (
                <Column width="content">
                  <span className="italic">
                    <Text variant="caption" tone="secondary" overflow="nowrap">
                      {audienceCount}
                    </Text>
                  </span>
                </Column>
              ) : null}
            </Columns>
            {!isPreview && isGroup && "lastActivityAt" in group.value ? (
              <LastActivityLabel lastActivityAt={group.value.lastActivityAt} showNoActivity />
            ) : null}
          </Stack>
        </Column>
      </Columns>
    </ContentBlock>
  );
}
